import faker from "faker";

import { AccountType } from "component-library";
import {
  ISelfTapeRequestStatus,
  MediaObject,
  MediaRequestType,
  MediaType,
  TranscodeStatus,
} from "shared-types";
import { DEFAULT_USER_TYPE } from "test-utilities/fakeHelpers";

import { anyHeightRange, anyPlayingAgeRange } from "shared/config/common";
import {
  IAgency,
  IAgentList,
  IAgentListInputModel,
  IAgentListsItem,
  IPaginatedAgentLists,
} from "shared/models/agentList";
import {
  DisabilityPreference,
  IBreakdown,
  IBreakdownLinkRoles,
  IBreakdownListItem,
  IBreakdownRole,
  ILinkAttachment,
  IncludePersonalDetails,
  IPaginatedBreakdownList,
  IRelatedBreakdownRoles,
  LinkmailType,
} from "shared/models/breakdown";
import { IFacet, IFacetCategory, IFacetGroup } from "shared/models/facet";
import {
  FollowUpIncludePersonalDetails,
  ICreateFollowUp,
  IFollowUp,
  IFollowUpRecipientListItem,
  IPaginatedFollowUpList,
  IPaginatedFollowUpRecipientList,
} from "shared/models/followUp";
import { ICountry } from "shared/models/location";
import { IProject } from "shared/models/project";
import {
  ISelfTapeRequest,
  ISelfTapeRequestCDView,
  ISelfTapeRequestInvite,
  ISelfTapeRequestInviteList,
  ISelfTapeRequestsList,
  ISubmission,
  ISubmissionDetails,
  ISubmissionMedia,
} from "shared/models/selfTapeRequest";
import {
  IAgentJobSettings,
  IJobSettings,
  IPerformerJobSettings,
  UserDisabilityPreference,
} from "shared/models/settings";
import { OutputGroupType } from "shared/models/media";

export const HOUR = 1000 * 60 * 60;
export const ONE_DAY = HOUR * 24;

export function getRandomString(): string {
  return faker.helpers.slugify(faker.lorem.word());
}

export function getRandomSentence(): string {
  return faker.lorem.sentence();
}

export function getRandomUuid(): string {
  return faker.random.uuid();
}

export function getRandomNumber(max?: number): number {
  return faker.random.number(max);
}

export function getRandomBoolean(): boolean {
  return faker.random.boolean();
}

export function getDateISOString(): string {
  return faker.date.past().toISOString();
}

export function getRandomUrl(): string {
  return `${faker.internet.url()}/`;
}

export function getRandomIntermediateDate(
  daysBeforeNow: number = 3,
  daysAfterNow: number = daysBeforeNow
) {
  const now = new Date();
  const startDay = new Date(now.getTime() - ONE_DAY * daysBeforeNow);
  const endDay = new Date(now.getTime() + ONE_DAY * daysAfterNow);
  return faker.date.between(startDay, endDay);
}

export function getProject(): IProject {
  return {
    id: getRandomUuid(),
    name: getRandomString(),
    dateUpserted: getDateISOString(),
    activeBreakdownCount: getRandomNumber(),
    totalBreakdownCount: getRandomNumber(),
  };
}

export function getProjectList(count: number): IProject[] {
  return Array(count)
    .fill(1)
    .map(() => getProject());
}

export function getBreakdownListItem(): IBreakdownListItem {
  return {
    id: getRandomUuid(),
    title: getRandomString(),
    subject: getRandomString(),
    productionTypeId: getRandomNumber(),
    projectId: getRandomUuid(),
    projectName: getRandomString(),
    sent: getDateISOString(),
    created: getDateISOString(),
    closingDate: getDateISOString(),
    status: getRandomNumber(),
    rolesCount: getRandomNumber(),
    suggestionCount: getRandomNumber(),
    linkmailType: LinkmailType.Default,
    amendmentCount: getRandomNumber(),
    amendmentParentId: getRandomUuid(),
    roles: Array(2)
      .fill(1)
      .map(() => ({
        id: getRandomUuid(),
        name: getRandomString(),
        sequence: getRandomNumber(),
      })),
    agentListIds: Array(2)
      .fill(1)
      .map(() => getRandomNumber()),
    agentCategories: [],
    performerCategories: [],
    internationalDistributionPartners: [],
  };
}

export function getBreakdownListItems(
  count: number = 3
): IPaginatedBreakdownList {
  return {
    count,
    items: Array(count)
      .fill(1)
      .map(() => getBreakdownListItem()),
  };
}

export function getRelatedRoles(): IRelatedBreakdownRoles {
  return {
    id: getRandomUuid(),
    title: getRandomString(),
    linkmailType: LinkmailType.Amendment,
    amendmentCount: 2,
    roles: [
      {
        id: getRandomUuid(),
        name: getRandomString(),
      },
      {
        id: getRandomUuid(),
        name: getRandomString(),
      },
    ],
  };
}

export function getBreakdown(closingDate?: string): IBreakdown {
  return {
    id: getRandomUuid(),
    projectName: getRandomString(),
    sent: getDateISOString(),
    created: getDateISOString(),
    message: getRandomString(),
    spotlightExclusive: undefined!,
    agentCategories: [],
    performerCategories: [],
    castingTypes: [],
    castingLocations: [],
    castingTypeOtherDetails: getRandomString(),
    internationalDistributionPartners: [],
    dateUpserted: getDateISOString(),
    sentBy: getRandomString(),
    concurrencyToken: getRandomString(),
    roles: [],
    linkAttachments: [],
    subject: getRandomString(),
    castingCountryCode: getRandomString(),
    castingClosingDate: closingDate || getRandomString(),
    castingLocation: getRandomString(),
    castingDetails: getRandomString(),
    includePersonalDetailsToPerformers: IncludePersonalDetails.None,
    includePersonalDetailsToAgents: IncludePersonalDetails.None,
    linkmailType: LinkmailType.Default,
    amendmentCount: 0,
    amendmentParentId: null,
    production: {
      typeId: getRandomNumber(),
      title: getRandomString(),
      location: getRandomString(),
      countryCode: getRandomString(),
      contractTypeId: getRandomNumber(),
      summary: getRandomString(),
      dates: getRandomString(),
      director: getRandomString(),
      producer: getRandomString(),
      company: getRandomString(),
      castingDirector: getRandomString(),
      castingDirectorId: getRandomString(),
    },
    agentListIds: [],
  };
}

export function getBreakdownLinkRoles(): IBreakdownLinkRoles {
  return {
    links: [{ linkmailId: getRandomUuid(), roleId: getRandomUuid() }],
    concurrencyToken: getRandomString(),
  };
}

export function getEmptyBreakdown(): IBreakdown {
  return {
    production: {},
    roles: [],
    agentCategories: [],
    performerCategories: [],
    internationalDistributionPartners: [],
  } as unknown as IBreakdown;
}

export interface IRoleOptions {
  invalid?: boolean;
  contractTypeId?: number;
}

export function getRole(options?: IRoleOptions): IBreakdownRole {
  return {
    role: {
      id: getRandomUuid(),
      name: getRandomString(),
      description: getRandomString(),
      anyGender: true,
      genderIds: [],
      genderInfo: getRandomString(),
      disabilityPreference: DisabilityPreference.Undefined,
      disabilityIds: [],
      disabilityInfo: getRandomString(),
      minimumHeight: anyHeightRange.min,
      maximumHeight: anyHeightRange.max,
      minimumPlayingAge: 3,
      maximumPlayingAge: 99,
      agreementAccepted: !options?.invalid,
      anyAppearance: true,
      appearanceIds: [],
      contractTypeId: options?.contractTypeId ?? 0,
      contractTypeInfo: getRandomString(),
      linkAttachments: [],
      anyEthnicity: true,
      ethnicityLabel: "",
    },
    sequence: 0,
  };
}

export function getRoles(
  count: number = 3,
  options?: IRoleOptions
): IBreakdownRole[] {
  return Array(count)
    .fill(1)
    .map(() => getRole(options));
}

export function getCountry(): ICountry {
  return {
    localisedCountryName: getRandomString(),
    countryCode: getRandomString(),
  };
}

export function getCountries(count: number = 10): ICountry[] {
  return Array(count)
    .fill(1)
    .map(() => getCountry());
}

export function getFacets(count: number = 3): IFacet[] {
  return Array(count)
    .fill(1)
    .map((item, index) => ({
      id: index + 1,
      name: getRandomString(),
      code: getRandomString(),
      orderKey: count - index - 1,
    }));
}

export function getFacetGroups(
  count: number = 3,
  facetCount: number = 3
): IFacetGroup[] {
  return Array(count)
    .fill(1)
    .map(() => ({
      id: getRandomNumber(),
      name: getRandomString(),
      code: getRandomString(),
      facets: getFacets(facetCount),
    }));
}

export function getFacetCategories(
  facetCategories: string[]
): IFacetCategory[] {
  return facetCategories.map((category) => ({
    systemName: category,
    id: getRandomNumber(),
    name: category,
    code: "appearance",
    maxActiveCount: 1,
    facetGroups: getFacetGroups(),
  }));
}

export function getLinkAttachments(count: number = 3): ILinkAttachment[] {
  return Array(count)
    .fill(1)
    .map(() => ({
      attachmentId: getRandomUuid(),
      originalFileName: getRandomString(),
      url: getRandomString(),
      sequence: getRandomNumber(),
    }));
}

export function getFollowUpRecipientListItem(): IFollowUpRecipientListItem {
  return {
    fullName: getRandomString(),
    agencyName: getRandomString(),
  };
}

export function getFollowUpRecipientList(
  count: number = 3
): IPaginatedFollowUpRecipientList {
  return {
    count,
    items: Array(count)
      .fill(1)
      .map(() => getFollowUpRecipientListItem()),
  };
}

export function getCreateFollowUp(): ICreateFollowUp {
  return {
    subject: getRandomString(),
    body: getRandomString(),
    includePersonalDetails: 0,
  };
}

export function getFollowUp(): IFollowUp {
  return {
    id: getRandomUuid(),
    subject: getRandomString(),
    body: getRandomString(),
    created: getRandomString(),
    linkmailId: getRandomUuid(),
    productionTypeId: getRandomNumber(),
    productionTitle: getRandomString(),
    linkmailType: getRandomNumber(),
    amendmentCount: getRandomNumber(),
    includePersonalDetails: FollowUpIncludePersonalDetails.NameAndEmail,
    linkmailSentDate: getRandomString(),
    sentBy: getRandomString(),
  };
}

export function getAgency(): IAgency {
  return {
    name: getRandomString(),
    agencyCategories: [],
    blockLinkAccess: getRandomString(),
    id: getRandomNumber(),
  };
}

export function getAgencies(count: number = 3): IAgency[] {
  return Array(count)
    .fill(1)
    .map(() => getAgency());
}

export function getAgentList(count: number = 3): IAgentList {
  return {
    id: getRandomNumber(),
    name: getRandomString(),
    comment: getRandomString(),
    created: getRandomString(),
    modified: getRandomString(),
    archived: getRandomString(),
    deleted: getRandomString(),
    isTemp: false,
    agencies: getAgencies(count),
  };
}

export function getAgentListsItem(): IAgentListsItem {
  return {
    id: getRandomNumber(),
    name: getRandomString(),
    modified: getRandomString(),
    agentsNumber: getRandomNumber(),
  };
}

export function getAgentLists(count: number = 3): IPaginatedAgentLists {
  return {
    count,
    items: Array(count)
      .fill(1)
      .map(() => getAgentListsItem()),
  };
}

export function getFollowUpList(count: number = 3): IPaginatedFollowUpList {
  return {
    count,
    items: Array(count)
      .fill(1)
      .map(() => getFollowUp()),
  };
}

export function getAgentListUpdate(): IAgentListInputModel {
  return {
    name: getRandomString(),
    agencies: Array(3)
      .fill(1)
      .map(() => getRandomNumber()),
  };
}

export function getFile() {
  return new File([getRandomString()], getRandomString());
}

export function getAgentJobSettings(count: number = 3): IAgentJobSettings {
  const isAny = count === 0;
  return {
    anyContractType: isAny,
    contractTypes: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    anyProductionType: isAny,
    productionTypes: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    anyProductionCountry: isAny,
    productionCountries: Array(count)
      .fill(1)
      .map(() => getRandomString()),
    anyCastingCountry: isAny,
    castingCountries: Array(count)
      .fill(1)
      .map(() => getRandomString()),
    minPlayingAge: anyPlayingAgeRange.min,
    maxPlayingAge: anyPlayingAgeRange.max,
  };
}

export function getPerformerJobSettings(
  count: number = 3
): IPerformerJobSettings {
  const isAny = count === 0;
  return {
    anyContractType: isAny,
    contractTypes: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    anyProductionType: isAny,
    productionTypes: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    anyProductionCountry: isAny,
    productionCountries: Array(count)
      .fill(1)
      .map(() => getRandomString()),
    anyCastingCountry: isAny,
    castingCountries: Array(count)
      .fill(1)
      .map(() => getRandomString()),
    minPlayingAge: anyPlayingAgeRange.min,
    maxPlayingAge: anyPlayingAgeRange.max,
    minimumHeight: anyHeightRange.min,
    maximumHeight: anyHeightRange.max,
    anyGender: isAny,
    genderIds: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    anyAppearance: isAny,
    appearanceIds: Array(count)
      .fill(1)
      .map(() => getRandomNumber()),
    disabilityPreference: UserDisabilityPreference.Any,
    disabilityIds: [],
    anyEthnicity: isAny,
    heritageAndEthnicityIds: [],
    ethnicBackgroundIds: [],
  };
}

export function getJobSettings(
  userType: AccountType = DEFAULT_USER_TYPE
): IJobSettings {
  return {
    agentSettings:
      userType === AccountType.Agent ? getAgentJobSettings(0) : null,
    performerSettings:
      userType === AccountType.Performer ? getPerformerJobSettings(0) : null,
  };
}

export function getSelfTapeRequest(
  withSubmissionMedia?: boolean
): ISelfTapeRequest {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 1);

  return {
    id: getRandomUuid(),
    performerRequestId: getRandomUuid(),
    breakdownId: getRandomUuid(),
    projectId: getRandomUuid(),
    title: getRandomString(),
    expiryDate: expiryDate.toISOString(),
    description: getRandomString(),
    videosRequired: [
      {
        id: getRandomUuid(),
        instructions: getRandomString(),
      },
    ],
    anyAmount: false,
    rolesIds: [getRandomUuid()],
    attachments: [
      {
        id: getRandomUuid(),
        title: getRandomString(),
        s3ObjectKey: getRandomString(),
        mimeType: getRandomString(),
        url: getRandomString(),
      },
    ],
    createdAt: getDateISOString(),
    modifiedAt: getDateISOString(),
    modifiedBy: getRandomUuid(),
    breakdownTitle: getRandomString(),
    castingDirector: {
      id: getRandomUuid(),
      name: getRandomString(),
    },
    performer: {
      id: getRandomUuid(),
      name: getRandomString(),
      profileUri: getRandomString(),
      artistRef: getRandomString(),
    },
    roles: [
      {
        id: getRandomUuid(),
        name: getRandomString(),
      },
      {
        id: getRandomUuid(),
        name: getRandomString(),
      },
    ],
    submissionDate: getDateISOString(),
    submissionMedia: withSubmissionMedia ? [getSubmissionMedia()] : [],
    projectStatus: "Active",
    isPublished: false,
    status: ISelfTapeRequestStatus.Awaiting,
    mediaRequestType: MediaRequestType.SelfTape,
    suggestedBy: getRandomString(),
    performerReplyBy: getRandomString(),
    performerReplyTimestamp: new Date().toISOString(),
    forwardedToPerformerTimestamp: new Date().toISOString(),
  };
}

export function getSelfTapeRequestForArchivedProject(): ISelfTapeRequest {
  let selfTapeRequest = getSelfTapeRequest();
  selfTapeRequest.projectStatus = "Archived";

  return selfTapeRequest;
}

export function getSelfTapeRequestForDeletedProject(): ISelfTapeRequest {
  let selfTapeRequest = getSelfTapeRequest();
  selfTapeRequest.projectStatus = "Deleted";

  return selfTapeRequest;
}

export function getMediaObject(): MediaObject {
  return {
    ownerId: getRandomUuid(),
    transcodeStatus: TranscodeStatus.Complete,
    durationInMs: getRandomNumber(),
    outputFiles: [
      {
        width: getRandomNumber(),
        height: getRandomNumber(),
        durationInMs: getRandomNumber(),
        s3ObjectSize: getRandomNumber(),
        objectUrls: [getRandomString()],
        outputGroupType: OutputGroupType.FILE,
      },
    ],
    thumbnails: [getRandomUrl()],
    id: getRandomUuid(),
    originalFileName: getRandomString(),
    s3ObjectKey: getRandomString(),
    size: getRandomNumber(),
    mimeType: getRandomString(),
    mediaType: MediaType.Video,
    created: getRandomIntermediateDate(),
    modified: getRandomIntermediateDate(),
  };
}

export function getSubmissionMedia(): ISubmissionMedia {
  return {
    id: getRandomUuid(),
    mediaInfoId: getRandomUuid(),
    requiredMediaId: getRandomUuid(),
    title: getRandomString(),
    comments: getRandomString(),
  };
}

export function getSelfTapeRequests(
  count: number = 3
): ISelfTapeRequestCDView[] {
  return Array(count)
    .fill(1)
    .map(() => {
      return { ...getSelfTapeRequest(), inviteCount: getRandomNumber() };
    });
}

export function getSelfTapeRequestsList(
  count: number = 3
): ISelfTapeRequestsList {
  return {
    pageIndex: 0,
    pageSize: count,
    totalResults: count * 3,
    results: getSelfTapeRequests(count),
  };
}

export function getSelfTapeRequestInvite(
  withInfo: boolean = true
): ISelfTapeRequestInvite {
  return {
    id: getRandomUuid(),
    requestId: getRandomUuid(),
    performerId: getRandomUuid(),
    requestedAt: getRandomString(),
    status: ISelfTapeRequestStatus.Awaiting,
    agencyRef: getRandomNumber(),
    agencyName: getRandomString(),
    agencyEmail: getRandomString(),
    showPerformerEmail: true,
    info: withInfo
      ? {
          id: getRandomUuid(),
          artistRef: getRandomUuid(),
          forename: getRandomString(),
          surname: getRandomString(),
          email: getRandomString(),
          publicationCode: getRandomNumber(),
          mainAgent: {
            agentRef: getRandomNumber(),
            name: getRandomString(),
            email: getRandomString(),
            phone: getRandomString(),
            mobilePhone: getRandomString(),
            website: getRandomString(),
          },
        }
      : undefined,
  };
}

export function getSelfTapeRequestInvitations(
  count: number = 3
): ISelfTapeRequestInvite[] {
  return Array(count)
    .fill(1)
    .map(() => getSelfTapeRequestInvite());
}

export function getSelfTapeRequestInviteList(
  count: number = 3
): ISelfTapeRequestInviteList {
  return {
    pageIndex: 0,
    pageSize: count,
    totalResults: count * 3,
    results: getSelfTapeRequestInvitations(count),
  };
}

export function getSubmissionDetails(): ISubmissionDetails {
  return {
    breakdownId: getRandomUuid(),
    projectId: getRandomUuid(),
    videos: [
      {
        mediaInfoId: getRandomUuid(),
        requiredMediaId: getRandomUuid(),
        title: getRandomString(),
        comments: getRandomString(),
      },
    ],
  };
}

export function getSubmission(): ISubmission {
  return {
    id: getRandomUuid(),
    breakdownId: getRandomUuid(),
    projectId: getRandomUuid(),
    performerId: getRandomUuid(),
    requestId: getRandomUuid(),
    isPublished: false,
    submissionDate: getDateISOString(),
    videos: [
      {
        id: getRandomUuid(),
        mediaInfoId: getRandomUuid(),
        requiredMediaId: getRandomUuid(),
        title: getRandomString(),
        comments: getRandomString(),
      },
    ],
  };
}
